import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getConnectedAttendees } from '../../services/connectionService';
import { Link } from 'react-router-dom';

const ConnectedAttendees = ({ event }) => {
  const { currentUser } = useAuth();
  const [connectedAttendees, setConnectedAttendees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    if (currentUser && event?.id) {
      loadConnectedAttendees();
    }
  }, [currentUser, event?.id]);

  const loadConnectedAttendees = async () => {
    try {
      setLoading(true);
      const connected = await getConnectedAttendees(event.id, currentUser.uid);
      setConnectedAttendees(connected);
    } catch (error) {
      console.error('Error loading connected attendees:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Network at This Event</h3>
        <div className="animate-pulse">
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (connectedAttendees.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Network at This Event</h3>
        <div className="text-center py-8">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.196-2.121M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.196-2.121M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          <h4 className="mt-4 text-lg font-medium text-gray-900">No connections attending</h4>
          <p className="mt-2 text-gray-500">
            None of your network connections are registered for this event yet.
          </p>
          <Link
            to="/directory"
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-apn-blue bg-blue-50 hover:bg-blue-100"
          >
            Explore Member Directory
          </Link>
        </div>
      </div>
    );
  }

  const displayedAttendees = showAll ? connectedAttendees : connectedAttendees.slice(0, 5);

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Your Network at This Event</h3>
        <span className="text-sm text-gray-500">
          {connectedAttendees.length} connection{connectedAttendees.length !== 1 ? 's' : ''} attending
        </span>
      </div>

      <div className="space-y-4">
        {displayedAttendees.map((attendee, index) => (
          <div key={attendee.id || index} className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
            <div className="flex-shrink-0">
              {attendee.profile?.profilePhoto ? (
                <img
                  src={attendee.profile.profilePhoto}
                  alt={`${attendee.profile.firstName} ${attendee.profile.lastName}`}
                  className="w-10 h-10 rounded-full object-cover"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-apn-blue flex items-center justify-center">
                  <span className="text-white font-medium text-sm">
                    {attendee.profile?.firstName?.[0] || attendee.firstName?.[0] || '?'}
                    {attendee.profile?.lastName?.[0] || attendee.lastName?.[0] || ''}
                  </span>
                </div>
              )}
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {attendee.profile?.firstName || attendee.firstName} {attendee.profile?.lastName || attendee.lastName}
                </p>
                {attendee.profile?.membershipTier && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                    {attendee.profile.membershipTier}
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-500 truncate">
                {attendee.profile?.title || attendee.title}
                {attendee.profile?.company && ` at ${attendee.profile.company}`}
              </p>
              <p className="text-xs text-gray-400">
                Registered as: {attendee.registrationType || 'Member'}
              </p>
            </div>

            <div className="flex-shrink-0">
              <Link
                to={`/directory/${attendee.userId}`}
                className="text-apn-blue hover:text-blue-700 text-sm font-medium"
              >
                View Profile
              </Link>
            </div>
          </div>
        ))}
      </div>

      {connectedAttendees.length > 5 && (
        <div className="mt-4 text-center">
          <button
            onClick={() => setShowAll(!showAll)}
            className="text-apn-blue hover:text-blue-700 text-sm font-medium"
          >
            {showAll ? 'Show Less' : `Show All ${connectedAttendees.length} Connections`}
          </button>
        </div>
      )}

      {/* Network Insights */}
      {connectedAttendees.length > 0 && (
        <div className="mt-6 pt-4 border-t border-gray-200">
          <h4 className="text-sm font-medium text-gray-900 mb-2">Network Insights</h4>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Companies represented:</span>
              <span className="ml-1 font-medium">
                {new Set(connectedAttendees.map(a => a.profile?.company).filter(Boolean)).size}
              </span>
            </div>
            <div>
              <span className="text-gray-500">Registration types:</span>
              <span className="ml-1 font-medium">
                {new Set(connectedAttendees.map(a => a.registrationType)).size}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConnectedAttendees;
